import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { AmountInput } from "@/features/cost-allocations/components/AmountInput";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { ChevronLeft } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRegisterAnimal } from "../api/animalsApi";
import {
  INITIAL_LIFECYCLE_STATES_BY_GROUP,
  LIFECYCLE_GROUP_LABEL,
  LIFECYCLE_LABEL,
} from "../lifecycleStates";
import type { LifecycleState } from "../types";
import { AnimalNotFoundPage } from "./AnimalNotFoundPage";

const ALLOWED_ROLES = ["owner", "admin", "operator"] as const;

const AUTO_CLASSIFY = "auto" as const;

type AcquisitionType = "born" | "purchased";

export function RegisterAnimalPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const role = currentMembership?.role;
  const navigate = useNavigate();

  // Anti-enumeración: viewer ve un 404, no un 403, para no revelar la
  // existencia de la ruta de write.
  if (role && !ALLOWED_ROLES.includes(role as (typeof ALLOWED_ROLES)[number])) {
    return <AnimalNotFoundPage />;
  }

  return <RegisterAnimalForm accountId={accountId} navigate={navigate} />;
}

function RegisterAnimalForm({
  accountId,
  navigate,
}: { accountId: string; navigate: ReturnType<typeof useNavigate> }) {
  const lotsQuery = useLotsList(accountId);
  const mutation = useRegisterAnimal(accountId);

  const [tag, setTag] = useState("");
  const [name, setName] = useState("");
  const [sex, setSex] = useState<"female" | "male">("female");
  const [birthDate, setBirthDate] = useState<Date | undefined>(undefined);
  const [birthDateIsEstimated, setBirthDateIsEstimated] = useState(false);
  const [lotId, setLotId] = useState("");
  const [acquisitionType, setAcquisitionType] = useState<AcquisitionType>("born");
  const [initialCost, setInitialCost] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "credit">("credit");
  const [lifecycleChoice, setLifecycleChoice] = useState<LifecycleState | typeof AUTO_CLASSIFY>(
    AUTO_CLASSIFY,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const activeLots = (lotsQuery.data?.lots ?? []).filter((l) => l.isActive);
  const today = new Date();

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!tag.trim()) next.tag = "El tag es obligatorio.";
    if (!birthDate) next.birthDate = "La fecha de nacimiento es obligatoria.";
    if (birthDate && birthDate.getTime() > today.getTime()) {
      next.birthDate = "La fecha de nacimiento no puede ser futura.";
    }
    if (!lotId) next.lotId = "Seleccioná un lote.";
    if (acquisitionType === "purchased") {
      if (!initialCost || Number(initialCost) <= 0) {
        next.initialCost = "El monto de compra es obligatorio.";
      }
    }
    return next;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    if (!birthDate) return; // narrowed by validate() but TS no lo sabe

    const payload = {
      tag: tag.trim(),
      name: name.trim() || undefined,
      sex,
      birthDate: formatDateOnly(birthDate),
      birthDateIsEstimated,
      lotId,
      acquisitionType,
      initialCost: acquisitionType === "purchased" ? initialCost : undefined,
      lifecycleState: lifecycleChoice === AUTO_CLASSIFY ? undefined : lifecycleChoice,
      paymentMethod: acquisitionType === "purchased" ? paymentMethod : undefined,
    };

    try {
      const result = await mutation.mutateAsync(payload);
      toast.success("Animal registrado.");
      navigate(`/app/hato/${result.animal.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === "TAG_ALREADY_EXISTS") {
          setErrors({ tag: "Este tag ya existe en la cuenta." });
          return;
        }
        if (err.status === 404 && err.code === "LOT_NOT_FOUND") {
          setErrors({ lotId: "El lote ya no está disponible." });
          return;
        }
        if (err.status === 400) {
          toast.error("Revisá los datos del formulario.");
          return;
        }
      }
      toast.error("No se pudo registrar el animal. Intentá de nuevo.");
    }
  }

  return (
    <>
      <title>Registrar animal · Hato · HATO</title>
      <Breadcrumb />

      <div className="mt-4 max-w-xl">
        <header className="mb-5">
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Registrar animal</h1>
          <p className="mt-1 text-app-ink-3 text-sm">
            Para alta individual. Para varios animales a la vez, usá{" "}
            <Link to="/app/hato/import" className="underline hover:text-app-ink-1">
              Import CSV
            </Link>
            .
          </p>
        </header>

        <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-5">
          <Field label="Tag" required error={errors.tag} htmlFor="tag-input">
            <input
              id="tag-input"
              type="text"
              autoComplete="off"
              value={tag}
              onChange={(e) => {
                setTag(e.target.value);
                if (errors.tag) setErrors((prev) => omit(prev, "tag"));
              }}
              maxLength={100}
              aria-invalid={Boolean(errors.tag)}
              className="h-10 w-full rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
            />
          </Field>

          <Field label="Nombre" htmlFor="name-input" hint="Opcional">
            <input
              id="name-input"
              type="text"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="h-10 w-full rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
            />
          </Field>

          <Field label="Sexo" required>
            <Select value={sex} onValueChange={(v) => setSex(v as "female" | "male")}>
              <SelectTrigger className="h-10 w-full text-app-ink-1" aria-label="Sexo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Hembra</SelectItem>
                <SelectItem value="male">Macho</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Fecha de nacimiento" required error={errors.birthDate}>
            <DatePicker
              value={birthDate}
              onChange={(d) => {
                setBirthDate(d);
                if (errors.birthDate) setErrors((prev) => omit(prev, "birthDate"));
              }}
              maxDate={today}
              aria-label="Fecha de nacimiento"
            />
            <label className="mt-2 inline-flex items-center gap-2 text-app-ink-2 text-sm">
              <input
                type="checkbox"
                checked={birthDateIsEstimated}
                onChange={(e) => setBirthDateIsEstimated(e.target.checked)}
                className="h-4 w-4 accent-app-emerald"
              />
              Fecha estimada
            </label>
          </Field>

          <Field label="Lote" required error={errors.lotId}>
            <Select
              value={lotId}
              onValueChange={(v) => {
                setLotId(v);
                if (errors.lotId) setErrors((prev) => omit(prev, "lotId"));
              }}
              disabled={lotsQuery.isLoading || activeLots.length === 0}
            >
              <SelectTrigger className="h-10 w-full text-app-ink-1" aria-label="Lote">
                <SelectValue
                  placeholder={
                    lotsQuery.isLoading
                      ? "Cargando lotes…"
                      : activeLots.length === 0
                        ? "No hay lotes activos"
                        : "Seleccionar lote"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {activeLots.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Etapa de vida" hint="Por defecto se autoclasifica.">
            <Select
              value={lifecycleChoice}
              onValueChange={(v) =>
                setLifecycleChoice(v === AUTO_CLASSIFY ? AUTO_CLASSIFY : (v as LifecycleState))
              }
            >
              <SelectTrigger className="h-10 w-full text-app-ink-1" aria-label="Etapa de vida">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_CLASSIFY}>Auto-clasificar</SelectItem>
                {(["cria", "desarrollo", "productiva", "retirada"] as const).map((g) => {
                  const states = INITIAL_LIFECYCLE_STATES_BY_GROUP[g];
                  if (states.length === 0) return null;
                  return (
                    <SelectGroup key={g}>
                      <SelectLabel>{LIFECYCLE_GROUP_LABEL[g]}</SelectLabel>
                      {states.map((s) => (
                        <SelectItem key={s} value={s}>
                          {LIFECYCLE_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Origen" required>
            <Select
              value={acquisitionType}
              onValueChange={(v) => {
                const next = v as AcquisitionType;
                setAcquisitionType(next);
                if (next !== "purchased") {
                  setInitialCost("");
                  setPaymentMethod("credit");
                }
                if (errors.initialCost) setErrors((prev) => omit(prev, "initialCost"));
              }}
            >
              <SelectTrigger className="h-10 w-full text-app-ink-1" aria-label="Origen">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="born">Nacido en finca</SelectItem>
                <SelectItem value="purchased">Comprado</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {acquisitionType === "purchased" && (
            <>
              <Field label="Monto de compra" required error={errors.initialCost}>
                <AmountInput
                  value={initialCost}
                  onChange={(v) => {
                    setInitialCost(v);
                    if (errors.initialCost) setErrors((prev) => omit(prev, "initialCost"));
                  }}
                  aria-label="Monto de compra"
                  aria-invalid={Boolean(errors.initialCost)}
                />
              </Field>

              <Field
                label="Método de pago"
                required
                hint="Define el asiento contable: Cuentas por pagar (crédito) o Banco (contado)"
              >
                <fieldset aria-label="Método de pago" className="flex gap-2 border-0 p-0">
                  <PaymentMethodOption
                    value="credit"
                    label="Crédito"
                    selected={paymentMethod === "credit"}
                    onSelect={() => setPaymentMethod("credit")}
                  />
                  <PaymentMethodOption
                    value="cash"
                    label="Contado"
                    selected={paymentMethod === "cash"}
                    onSelect={() => setPaymentMethod("cash")}
                  />
                </fieldset>
              </Field>
            </>
          )}

          <div className="mt-2 flex items-center justify-end gap-3">
            <Link
              to="/app/hato"
              className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? "Registrando…" : "Registrar animal"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="font-medium text-[13px] text-app-ink-2">
        {label}
        {required && <span className="ml-0.5 text-app-ink-3">*</span>}
        {hint && <span className="ml-2 font-normal text-app-ink-3 text-xs">({hint})</span>}
      </label>
      {children}
      {error && (
        <p role="alert" className="text-[12px] text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

function PaymentMethodOption({
  value,
  label,
  selected,
  onSelect,
}: {
  value: "cash" | "credit";
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex-1 cursor-pointer rounded-lg border px-4 py-2.5 text-center text-base transition-colors ${
        selected
          ? "border-app-emerald bg-app-emerald/10 font-medium text-app-ink-1"
          : "border-app-border bg-app-surface text-app-ink-2 hover:bg-app-surface-2"
      }`}
    >
      <input
        type="radio"
        name="paymentMethod"
        value={value}
        checked={selected}
        onChange={onSelect}
        aria-label={label}
        className="sr-only"
      />
      {label}
    </label>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Migas de pan" className="flex items-center gap-2 text-[12px] text-app-ink-3">
      <Link to="/app/hato" className="inline-flex items-center gap-1 hover:text-app-ink-1">
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span>Hato</span>
      </Link>
      <span aria-hidden>›</span>
      <span className="text-app-ink-2">Registrar animal</span>
    </nav>
  );
}

function formatDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function omit<T extends Record<string, string>>(obj: T, key: string): T {
  const { [key]: _, ...rest } = obj;
  return rest as T;
}
